import angular from "angular";

import {
  Ajax,
  Calculation,
  Language,
  Numeric,
  calcReportType,
  ReportType,
  Translations,
  Reports,
  Objects
} from "@salaxy/core";
import { SessionService, SettingsService, UiHelpers } from "../../services";
import {
  CalculationMapper,
  CalculationReport,
  ESalaryMapper,
  ReportData,
  ReportOptions,
  Templates,
  TotalsReportConfigGenerator
} from "@salaxy/reports";
import { ColumnDefinition } from "tabulator-tables";
import { SxyTableOptions, SxyTableController } from "../sxy-table/SxyTableController";

/**
 * Renders a calculation report or a calculation summary report for the given calculation(s).
 * Uses Handlebars templates (@salaxy/reports).
 */
export class CalcReportController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Templates",
    "AjaxNg1",
    "$sce",
    "SettingsService",
    "$timeout",
    "SessionService",
    "Reports",
    "UiHelpers",
  ];

  /** The calculation(s) for the report. Todo add Calcs*/
  public calc: Calculation | Calculation[];

  /** Template for the report */
  public templateName: string;

  /** Boolean indicating if the refresh is in progress. */
  public refreshInprogress: boolean;

  /**Selected report language */
  public lang: Language;

  /** If true, adds the preview watermark to the preview. */
  public preview?: boolean;

  /**
   * Function that is called when the report type has been changed
   * Function has the following locals:  reportType: the selected report type.
   */
  public onReportTypeChange: (params: {
    /** Selected reportType */
    reportType: ReportType | calcReportType,
  }) => void;

  /** Ref to used SxyTable */
  public $sxyTable: SxyTableController;

  /** The type for the calculation report. */
  private _reportType: ReportType | calcReportType;

  private renderedCalc: Calculation | Calculation[];

  private renderedOptions: string;

  private renderedLang: string;

  private htmlData: any;

  private reportOptions: ReportOptions;

  private reportData: ReportData<CalculationReport> = {
    headerFooter: {},
    layout: {},
    report: {},
  };
  private isXslLoading: boolean;
  private xslText: string;

  private sxyTableConfig: { currentCalc: Calculation | Calculation[], reportLang: Language, config: { options: SxyTableOptions, include: ColumnDefinition[], data: any[] } } = {
    currentCalc: null,
    reportLang: Language.Default,
    config: null
  };

  /**
   * Creates a new CalcReportController
   * @ignore
   */
  constructor(
    private templates: Templates,
    private ajax: Ajax,
    private $sce: angular.ISCEService,
    private settingsService: SettingsService,
    private $timeout: angular.ITimeoutService,

    private sessionService: SessionService,
    private reportsApi: Reports,

    private uiHelpers: UiHelpers
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    // no initializations
  }

  /** The type for the calculation report. */
  public get reportType(): ReportType | calcReportType {
    return this._reportType;
  }

  /** The type for the calculation report. */
  public set reportType(value: ReportType | calcReportType) {
    if (value != this._reportType) {
      this._reportType = value;
      if (this.onReportTypeChange) {
        this.onReportTypeChange({ reportType: value });
      }
    }
  }

  /** Bindable and trusted html. */
  public get html(): any {
    if (!this.calc) {
      this.renderedCalc = null;
      this.renderedOptions = null;
      this.renderedLang = null;
    } else {
      const calc = angular.copy(this.calc);

      if (!this.renderedCalc ||
        !angular.equals(calc, this.renderedCalc) ||
        this.renderedOptions !== this.reportType + ";" + this.templateName ||
        this.renderedLang !== this.lang) {
        this.renderedCalc = angular.copy(calc);
        this.renderedOptions = this.reportType + ";" + this.templateName;
        this.renderedLang = this.lang;
        this.getDefaultLanguage(this.lang).then((lang) => {
          this.lang = lang;
          this.renderedLang = this.lang;
          // Refresh html reports
          if (this.reportType !== ReportType.ESalarySpecification) {
            this.refresh();
          }
          // Refresh eSalary xml
          if (this.reportType === ReportType.ESalarySpecification && !this.isXslLoading) {
            this.isXslLoading = true;
            if (!this.xslText) {
              fetch(`https://cdn.salaxy.com/reports/PayslipCommon20.xsl`)
                .then(response => response.text())
                .then(text => {
                  this.xslText = text;
                  this.refreshESalaryXml();
                });
            } else {
              this.refreshESalaryXml();
            }
          }
          return;
        });
      }
    }
    return this.htmlData;
  }

  /**
   * Config for SxyTable
   */
  public get tableConfig(): { options: SxyTableOptions, include: ColumnDefinition[], data: any[] } {
    if (!this.calc) {
      this.sxyTableConfig = {
        currentCalc: null,
        reportLang: null,
        config: null
      };
    } else {
      const calcs = angular.copy(this.calc);
      if (!Objects.equal(this.sxyTableConfig.currentCalc, calcs) || this.lang !== this.sxyTableConfig.reportLang) {
        this.sxyTableConfig.currentCalc = calcs;
        this.sxyTableConfig.reportLang = this.lang;
        this.getDefaultLanguage(this.lang).then((lang) => {
          this.lang = lang;
          this.renderedLang = this.lang;
          this.refreshTableConfig();
        });
      }
    }
    return this.sxyTableConfig.config;
  }

  /**
   * Method used to refresh table config
   */
  public async refreshTableConfig() {
    const renderingLang = this.lang ?? Language.Fi;
    await Translations.loadLanguage(renderingLang);
    this.sxyTableConfig.config = TotalsReportConfigGenerator.getConfig(this.calc, renderingLang);
    await this.$timeout();
    this.$sxyTable.refresh();
  }

  private async refreshESalaryXml() {
    const calc = await this.resolveCumulativeCalculation(Array.isArray(this.calc) ? this.calc[0] : this.calc);
    const xml = this.templates.getHtml("eSalary", ESalaryMapper.getESalary(calc), this.lang ?? Language.Fi);
    const xsltProcessor = new XSLTProcessor();
    const xslDoc = new window.DOMParser().parseFromString(this.xslText, "text/xml");
    const xmlDoc = new window.DOMParser().parseFromString(xml, "text/xml");
    xsltProcessor.importStylesheet(xslDoc);
    const resultDocument = xsltProcessor.transformToDocument(xmlDoc);
    this.htmlData = this.$sce.trustAsHtml(resultDocument.documentElement.innerHTML);
    this.isXslLoading = false;
    this.$timeout();
  }

  private async resolveCumulativeCalculation(calc: Calculation): Promise<Calculation> {
    if (calc.id) {
      calc = angular.copy(calc);
      const cumulativeCalc = await this.reportsApi.getCumulativeCalculation(calc.id);
      (calc as any).cumulative = cumulativeCalc.cumulative;
      return Promise.resolve(calc);
    }
    return Promise.resolve(calc);
  }

  /**
   * Refresh calculation report data.
   */
  private async refresh() {
    if (this.refreshInprogress) {
      return;
    }
    this.refreshInprogress = true;
    this.settingsService.getReportOptions().then(async (options) => {
      this.reportOptions = options;
      const renderingLang = this.lang ?? Language.Fi;
      await Translations.loadLanguage(renderingLang);
      if (Array.isArray(this.calc)) {
        this.reportData.report = CalculationMapper.getCalculationSummaryReport(this.calc, renderingLang);
        this.reportData.headerFooter = CalculationMapper.getCalculationSummaryReportHeaderFooter(this.calc, this.reportOptions, renderingLang);
      } else {
        this.reportData.report = CalculationMapper.getCalculationReport(this.calc, this.reportType as ReportType, renderingLang);
        this.reportData.headerFooter = CalculationMapper.getHeaderFooter(this.reportType as ReportType, this.calc, this.reportOptions, renderingLang);
      }
      const reportOptions = this.getDefaultReportOptions(angular.copy(this.reportOptions));
      this.reportData.layout = CalculationMapper.getReportLayout(false, false, this.preview ? "default" : undefined);
      // Special case (simple report), no mapper needed
      if (this.reportType === ReportType.TotalsReport) {
        this.reportData.report = Array.isArray(this.calc) ? this.calc as CalculationReport : [this.calc] as CalculationReport;
        this.reportData.headerFooter = CalculationMapper.getTotalReportHeaderFooter(Array.isArray(this.calc) ? this.calc[0] : this.calc, this.reportOptions, renderingLang);
        this.reportData.layout = CalculationMapper.getReportLayout(true, false, this.preview ? "default" : undefined);
      }
      const css = "";
      this.reportData.layout.hasCustomCss = false;
      this.reportData.layout.customCss = (css || "");
      if (this.preview) {
        this.reportData.layout.isPreview = true;
      }
      this.reportData.layout.margin = {
        top: reportOptions.margin.top,
        right: reportOptions.margin.right,
        bottom: reportOptions.margin.bottom,
        left: reportOptions.margin.left
      };
      const templates = new Templates();
      this.reportData.headerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.headerTemplate, this.reportData.headerFooter, renderingLang) : "";
      this.reportData.footerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.footerTemplate, this.reportData.headerFooter, renderingLang) : "";
      this.htmlData = this.$sce.trustAsHtml(this.templates.getHtml(this.templateName, this.reportData, renderingLang));
      this.refreshInprogress = false;
      this.$timeout();
    });

  }

  private getDefaultReportOptions(optionsInput?: ReportOptions): ReportOptions {
    optionsInput = optionsInput || {};
    optionsInput.margin = optionsInput.margin || {};
    optionsInput.margin.top = Numeric.parseNumber(optionsInput.margin.top) || 30;
    optionsInput.margin.right = Numeric.parseNumber(optionsInput.margin.right) || 10;
    optionsInput.margin.bottom = Numeric.parseNumber(optionsInput.margin.bottom) || 40;
    optionsInput.margin.left = Numeric.parseNumber(optionsInput.margin.left) || 10;
    return optionsInput;
  }

  private async getDefaultLanguage(lang: Language): Promise<Language> {
    if (!lang) {
      return this.sessionService.getReportLang();
    }
    return Promise.resolve(lang);
  }

}
